import React, { useMemo, useState } from 'react';
import { CheckCircle, Gift, RefreshCw, RotateCcw, UserCheck, UserPlus, WifiOff } from 'lucide-react';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import EmptyState from '../EmptyState';
import { MetricCard } from '../MetricCards';
import WalkInForm from './WalkInForm';
import { filterGuests, useDoor } from '../../hooks/useDoor';
import type { DoorApi, DoorCredentialSource } from '../../services/DoorApiClient';
import type { DoorFavour, DoorFilter, DoorGuest } from '../../types/Door';
import '../../styles/components/DoorScreen.css';

interface DoorScreenProps {
  api: DoorApi;
  eventId: string;
  /** Shown when the credentials are refused (a kiosk whose password was revoked offers sign-in again). */
  onSignInAgain?: () => void;
  pollMs?: number;
  /** Where the live socket gets its credentials. Without it the screen only polls. */
  credentials?: DoorCredentialSource;
}

const FILTERS: ReadonlyArray<{ value: DoorFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'not_arrived', label: 'Not arrived' },
  { value: 'checked_in', label: 'Checked in' },
  { value: 'walk_in', label: 'Walk-ins' },
];

function tableLabel(g: DoorGuest): string | null {
  if (!g.table) return null;
  return g.table.name || (g.table.number != null ? `Table ${g.table.number}` : 'Table');
}

/**
 * The door of one event: counts, search, filters, check-in and undo, walk-ins. Works the same for a signed-in planner and for a
 * kiosk device; only the DoorApi differs. Touch targets are 48px; the list is one column on a phone, two on a wide tablet.
 */
const DoorScreen: React.FC<DoorScreenProps> = ({ api, eventId, onSignInAgain, pollMs, credentials }) => {
  const door = useDoor(api, eventId, pollMs, credentials);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<DoorFilter>('all');
  const [walkIn, setWalkIn] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState<string | null>(null);

  const favoursByGuest = useMemo(() => {
    const map = new Map<string, DoorFavour[]>();
    for (const f of door.favours) map.set(f.guest_id, [...(map.get(f.guest_id) ?? []), f]);
    return map;
  }, [door.favours]);
  const visible = useMemo(() => filterGuests(door.guests, q, filter), [door.guests, q, filter]);

  if (door.status === 'loading') {
    return (
      <div className="door-state" role="status" aria-live="polite">
        <RefreshCw size={24} className="ed-spin" aria-hidden />
        <p>Loading the guest list…</p>
      </div>
    );
  }
  if (door.status === 'ended') {
    return (
      <div className="door-state" role="alert">
        <h3>This session has ended</h3>
        <p>The kiosk password may have been revoked or may have expired. Ask the event owner for a new one.</p>
        {onSignInAgain && <Button variant="primary" size="lg" onClick={onSignInAgain}>Sign in again</Button>}
      </div>
    );
  }
  if (door.status === 'missing') {
    return (
      <div className="door-state" role="alert">
        <h3>Event not found</h3>
        <p>This event is not available to this login. Check the link you were given.</p>
      </div>
    );
  }
  if (door.status === 'unavailable') {
    return (
      <div className="door-state">
        <LoadErrorBox what="the guest list" error={door.error ?? undefined} onRetry={() => { void door.refresh(); }} />
      </div>
    );
  }

  const { counts } = door;
  return (
    <div className="door fade-in">
      {door.stale && (
        <div className="door-banner" role="status">
          <WifiOff size={16} aria-hidden /> Not connected. Showing the list as it was a moment ago; it updates when the connection returns.
        </div>
      )}

      <p className="door-live" role="status">{door.live ? 'Live: changes from other devices appear at once.' : 'Updating every few seconds.'}</p>

      <div className="stats-grid door-counts">
        <MetricCard label="Expected" value={counts.expected} tone="blue" icon="users" />
        <MetricCard label="Checked in" value={`${counts.checked_in} / ${counts.expected}`} tone="green" icon="user-check" />
        <MetricCard label="Walk-ins" value={counts.walk_ins} tone="purple" icon="user-plus" />
        <MetricCard label="Not arrived" value={counts.not_arrived} tone="orange" icon="users" />
      </div>

      <div className="door-toolbar">
        <label className="door-search">
          <span className="door-sr">Search guests</span>
          <input
            className="door-input"
            type="search"
            inputMode="search"
            placeholder="Search by name or tag"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoComplete="off"
          />
        </label>
        <Button variant="primary" size="lg" icon={<UserPlus size={18} aria-hidden />} onClick={() => setWalkIn(true)}>Add walk-in</Button>
      </div>

      <div className="door-filters" role="group" aria-label="Filter the list">
        {FILTERS.map((f) => (
          <button key={f.value} type="button" className="door-chip" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>

      {walkIn && (
        <WalkInForm
          error={door.actionError}
          onClose={() => { setWalkIn(false); door.clearActionError(); }}
          onSubmit={door.addWalkIn}
        />
      )}

      {door.actionError && !walkIn && <p className="door-action-error" role="alert">{door.actionError}</p>}

      {visible.length === 0 ? (
        <EmptyState
          compact
          title={door.guests.length === 0 ? 'No guests on this event yet' : 'No one matches'}
          description={door.guests.length === 0 ? 'Guests invited to this event appear here. You can add a walk-in above.' : 'Clear the search or choose Everyone.'}
        />
      ) : (
        <ul className="door-list" aria-label="Guests">
          {visible.map((g) => {
            const table = tableLabel(g);
            const busy = door.busyId === g.id;
            return (
              <li key={g.id} className={`door-row${g.checked_in ? ' door-row--in' : ''}`}>
                <div className="door-row__main">
                  <div className="door-row__name">{g.name}</div>
                  <div className="door-row__meta">
                    {g.checked_in
                      ? <span className="door-badge door-badge--in"><CheckCircle size={12} aria-hidden /> Checked in</span>
                      : <span className="door-badge">Not arrived</span>}
                    {g.walk_in && <span className="door-badge door-badge--walkin">Walk-in</span>}
                    {g.rsvp_status === 'declined' && <span className="door-badge door-badge--warn">Declined</span>}
                    {table && <span className="door-badge">{table}</span>}
                    {g.plus_ones.length > 0 && <span className="door-badge">+{g.plus_ones.length}</span>}
                    {g.tags.map((t) => <span key={t} className="door-badge door-badge--tag">{t}</span>)}
                  </div>
                  {(favoursByGuest.get(g.id) ?? []).length > 0 && (
                    <ul className="door-favours" aria-label={`Favours for ${g.name}`}>
                      {(favoursByGuest.get(g.id) ?? []).map((f) => (
                        <li key={f.id} className="door-favour">
                          <Gift size={14} aria-hidden />
                          <span className="door-favour__name">{f.favour_name}{f.quantity > 1 ? ` ×${f.quantity}` : ''}</span>
                          {f.handed_over ? (
                            <Button variant="secondary" size="md" className="door-favour__btn" loading={door.busyId === f.id}
                              aria-label={`Take back ${f.favour_name} from ${g.name}`} onClick={() => { void door.takeBackFavour(f.id); }}>
                              Given. Undo
                            </Button>
                          ) : (
                            <Button variant="primary" size="md" className="door-favour__btn" loading={door.busyId === f.id}
                              aria-label={`Give ${f.favour_name} to ${g.name}`} onClick={() => { void door.giveFavour(f.id); }}>
                              Give
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="door-row__actions">
                  {!g.checked_in && (
                    <Button variant="primary" size="lg" className="door-btn" loading={busy} icon={<UserCheck size={18} aria-hidden />}
                      aria-label={`Check in ${g.name}`} onClick={() => { void door.checkIn(g.id); }}>
                      Check in
                    </Button>
                  )}
                  {g.checked_in && confirmUndo !== g.id && (
                    <Button variant="secondary" size="lg" className="door-btn" icon={<RotateCcw size={16} aria-hidden />}
                      aria-label={`Undo check-in for ${g.name}`} onClick={() => setConfirmUndo(g.id)}>
                      Undo
                    </Button>
                  )}
                  {g.checked_in && confirmUndo === g.id && (
                    <>
                      <Button variant="danger" size="lg" className="door-btn" loading={busy}
                        aria-label={`Confirm undo for ${g.name}`} onClick={() => { void door.undoCheckIn(g.id).then(() => setConfirmUndo(null)); }}>
                        Yes, undo
                      </Button>
                      <Button variant="secondary" size="lg" className="door-btn" onClick={() => setConfirmUndo(null)}>Keep</Button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DoorScreen;
